import { useMemo, useCallback, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useUserDashboardPreference,
  useSaveDashboardPreference,
  useResetDashboardPreference,
  dashboardPreferenceKeys,
} from './useUserDashboardPreference';
import { useSharedDashboardWidgets } from './useSharedDashboardWidgets';
import type { DashboardGridItem } from '@/components/dashboard/DashboardGrid';

export interface WidgetConfig {
  id: string;
  type: string;
  displayName: string;
  readModel?: string;
  measure?: string;
  measures?: Array<{ key: string; label: string; color?: string; yAxis?: 'left' | 'right' }>;
  aggregation?: string;
  groupBy?: string;
  sortBy?: string;
  sortDirection?: string;
  limit?: number;
  columns?: Array<{ field: string; label: string; width?: string }>;
  comparison?: { type: string; label?: string };
  display?: Record<string, unknown>;
  dimension?: string;
  permission?: string;
  layout: { x: number; y: number; w: number; h: number };
  source: 'dsl' | 'shared' | 'user';
}

export interface UserWidgetPreferences {
  hidden?: string[];
  layoutOverrides?: Record<string, { x: number; y: number; w: number; h: number }>;
  customWidgets?: WidgetConfig[];
}

export interface UseDashboardWidgetsResult {
  widgets: WidgetConfig[];
  layout: DashboardGridItem[];
  isLoading: boolean;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  onLayoutChange: (layout: DashboardGridItem[]) => void;
  addWidget: (widget: WidgetConfig) => void;
  updateWidget: (widgetId: string, updates: Partial<WidgetConfig>) => void;
  removeWidget: (widgetId: string) => void;
  saveLayout: () => Promise<void>;
  resetToDefaults: () => Promise<void>;
  isSaving: boolean;
}

export function useDashboardWidgets(
  dashboardName: string,
  defaultWidgets: WidgetConfig[],
): UseDashboardWidgetsResult {
  const [isEditing, setIsEditing] = useState(false);
  const [pendingLayout, setPendingLayout] = useState<DashboardGridItem[] | null>(null);
  const [pendingCustomWidgets, setPendingCustomWidgets] = useState<WidgetConfig[]>([]);
  const [pendingRemovedIds, setPendingRemovedIds] = useState<Set<string>>(new Set());
  const [pendingWidgetUpdates, setPendingWidgetUpdates] = useState<Record<string, Partial<WidgetConfig>>>({});

  const queryClient = useQueryClient();
  const preferenceQuery = useUserDashboardPreference(dashboardName);
  const sharedQuery = useSharedDashboardWidgets(dashboardName);
  const saveMutation = useSaveDashboardPreference(dashboardName);
  const resetMutation = useResetDashboardPreference(dashboardName);

  const userPrefs = useMemo<UserWidgetPreferences | null>(() => {
    const raw = preferenceQuery.data?.widgets;
    if (!raw) return null;
    try {
      return JSON.parse(raw) as UserWidgetPreferences;
    } catch {
      return null;
    }
  }, [preferenceQuery.data]);

  // 3-layer merge: DSL defaults → Shared widgets → User preferences
  const widgets = useMemo(() => {
    // Layer 1: DSL defaults
    const merged: WidgetConfig[] = defaultWidgets.map((w) => ({
      ...w,
      source: 'dsl' as const,
    }));

    // Layer 2: Append active shared widgets
    const sharedWidgets = sharedQuery.data ?? [];
    for (const sw of sharedWidgets) {
      try {
        const config = JSON.parse(sw.widgetConfig) as Partial<WidgetConfig>;
        merged.push({
          ...config,
          id: `shared-${sw.id}`,
          displayName: sw.title || config.displayName || 'Shared Widget',
          type: config.type || 'kpiCard',
          layout: config.layout || { x: 0, y: 100, w: 3, h: 1 },
          source: 'shared' as const,
        } as WidgetConfig);
      } catch {
        // Skip malformed shared widget configs
      }
    }

    // Layer 3: Apply user preferences
    if (userPrefs) {
      // Filter out hidden widgets and pending removals
      const hidden = new Set(userPrefs.hidden ?? []);
      const visible = merged.filter((w) => !hidden.has(w.id) && !pendingRemovedIds.has(w.id));

      // Append saved custom widgets (excluding pending removals)
      if (userPrefs.customWidgets) {
        for (const cw of userPrefs.customWidgets) {
          if (!pendingRemovedIds.has(cw.id)) {
            visible.push({ ...cw, source: 'user' as const });
          }
        }
      }

      // Append pending custom widgets (dedup against saved ones)
      const savedCustomIds = new Set((userPrefs.customWidgets ?? []).map((w) => w.id));
      for (const cw of pendingCustomWidgets) {
        if (!savedCustomIds.has(cw.id)) {
          visible.push({ ...cw, source: 'user' as const });
        }
      }

      // Apply layout overrides to ALL widgets (DSL, shared, and custom)
      if (userPrefs.layoutOverrides) {
        for (const w of visible) {
          const override = userPrefs.layoutOverrides[w.id];
          if (override) {
            w.layout = { ...w.layout, ...override };
          }
        }
      }

      // Apply pending widget config updates (for user/shared widgets)
      for (let i = 0; i < visible.length; i++) {
        const update = pendingWidgetUpdates[visible[i].id];
        if (update) {
          visible[i] = { ...visible[i], ...update, id: visible[i].id, source: visible[i].source };
        }
      }

      return visible;
    }

    // No prefs yet — filter pending removals and append pending custom widgets
    const result = merged.filter((w) => !pendingRemovedIds.has(w.id));
    for (const cw of pendingCustomWidgets) {
      result.push({ ...cw, source: 'user' as const });
    }

    // Apply pending widget config updates
    for (let i = 0; i < result.length; i++) {
      const update = pendingWidgetUpdates[result[i].id];
      if (update) {
        result[i] = { ...result[i], ...update, id: result[i].id, source: result[i].source };
      }
    }

    return result;
  }, [defaultWidgets, sharedQuery.data, userPrefs, pendingCustomWidgets, pendingRemovedIds, pendingWidgetUpdates]);

  const layout = useMemo<DashboardGridItem[]>(() => {
    const base = pendingLayout ?? widgets.map((w) => ({
      i: w.id,
      x: w.layout.x,
      y: w.layout.y,
      w: w.layout.w,
      h: w.layout.h,
    }));
    // Filter out removed widgets — react-grid-layout's onLayoutChange may
    // re-inject them with collapsed dimensions before React re-renders.
    return pendingRemovedIds.size > 0
      ? base.filter((item) => !pendingRemovedIds.has(item.i))
      : base;
  }, [widgets, pendingLayout, pendingRemovedIds]);

  const onLayoutChange = useCallback((newLayout: DashboardGridItem[]) => {
    // Filter out any items that have been removed — react-grid-layout may
    // report them with collapsed dimensions during the removal transition.
    setPendingLayout(
      pendingRemovedIds.size > 0
        ? newLayout.filter((item) => !pendingRemovedIds.has(item.i))
        : newLayout,
    );
  }, [pendingRemovedIds]);

  const addWidget = useCallback((widget: WidgetConfig) => {
    setPendingCustomWidgets((prev) => [...prev, widget]);
    // If there's a pending layout from drag/drop, add the new widget's layout to it
    setPendingLayout((prev) => {
      if (!prev) return prev;
      return [...prev, { i: widget.id, ...widget.layout }];
    });
  }, []);

  const updateWidget = useCallback((widgetId: string, updates: Partial<WidgetConfig>) => {
    // If it's a pending custom widget, update in place
    setPendingCustomWidgets((prev) => {
      const idx = prev.findIndex((w) => w.id === widgetId);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], ...updates };
        return updated;
      }
      return prev;
    });
    // Track as a pending update for saved custom/shared widgets
    setPendingWidgetUpdates((prev) => ({
      ...prev,
      [widgetId]: { ...prev[widgetId], ...updates },
    }));
  }, []);

  const removeWidget = useCallback((widgetId: string) => {
    // If it's a pending custom widget, just remove it from pending
    setPendingCustomWidgets((prev) => {
      const filtered = prev.filter((w) => w.id !== widgetId);
      return filtered.length < prev.length ? filtered : prev;
    });
    // Track removal for DSL/shared/saved-custom widgets
    setPendingRemovedIds((prev) => {
      const next = new Set(prev);
      next.add(widgetId);
      return next;
    });
    // Also remove from pending layout if present
    setPendingLayout((prev) => {
      if (!prev) return prev;
      return prev.filter((item) => item.i !== widgetId);
    });
  }, []);

  const saveLayout = useCallback(async () => {
    const layoutOverrides: Record<string, { x: number; y: number; w: number; h: number }> = {};
    if (pendingLayout) {
      for (const item of pendingLayout) {
        layoutOverrides[item.i] = { x: item.x, y: item.y, w: item.w, h: item.h };
      }
    }

    // Merge hidden: existing + newly hidden DSL/shared widgets
    const existingHidden = userPrefs?.hidden ?? [];
    const allHidden = [...new Set([...existingHidden, ...pendingRemovedIds])];

    // Merge custom widgets: existing (minus removed, with updates applied) + pending new ones
    const existingCustom = (userPrefs?.customWidgets ?? [])
      .filter((w) => !pendingRemovedIds.has(w.id))
      .map((w) => {
        const update = pendingWidgetUpdates[w.id];
        return update ? { ...w, ...update, id: w.id, source: w.source } : w;
      });
    const savedIds = new Set(existingCustom.map((w) => w.id));
    const newWidgets = pendingCustomWidgets.filter((w) => !savedIds.has(w.id));
    const allCustomWidgets = [...existingCustom, ...newWidgets];

    const prefs: UserWidgetPreferences = {
      ...userPrefs,
      hidden: allHidden.length > 0 ? allHidden : undefined,
      layoutOverrides: Object.keys(layoutOverrides).length > 0
        ? layoutOverrides
        : userPrefs?.layoutOverrides,
      customWidgets: allCustomWidgets.length > 0 ? allCustomWidgets : undefined,
    };

    try {
      await saveMutation.mutateAsync(JSON.stringify(prefs));
      // Refetch preferences from the server so userPrefs reflects the save.
      // Keep pendingCustomWidgets alive — the useEffect below will clear them
      // once the refetched userPrefs includes their IDs (dedup in the widgets
      // memo prevents duplicates in the meantime).
      await queryClient.invalidateQueries({
        queryKey: dashboardPreferenceKeys.byDashboard(dashboardName),
      });
      setPendingLayout(null);
      setPendingRemovedIds(new Set());
      setPendingWidgetUpdates({});
      setIsEditing(false);
    } catch {
      // Save failed — stay in edit mode
    }
  }, [pendingLayout, pendingCustomWidgets, pendingRemovedIds, pendingWidgetUpdates, userPrefs, saveMutation, queryClient, dashboardName]);

  // Auto-clear pending custom widgets once they appear in saved preferences
  useEffect(() => {
    if (pendingCustomWidgets.length === 0) return;
    const savedIds = new Set((userPrefs?.customWidgets ?? []).map((w) => w.id));
    const remaining = pendingCustomWidgets.filter((w) => !savedIds.has(w.id));
    if (remaining.length < pendingCustomWidgets.length) {
      setPendingCustomWidgets(remaining);
    }
  }, [userPrefs, pendingCustomWidgets]);

  const resetToDefaults = useCallback(async () => {
    try {
      await resetMutation.mutateAsync(undefined);
      // Refetch (will return null) so userPrefs clears.
      await queryClient.invalidateQueries({
        queryKey: dashboardPreferenceKeys.byDashboard(dashboardName),
      });
      setPendingLayout(null);
      setPendingCustomWidgets([]);
      setPendingRemovedIds(new Set());
      setPendingWidgetUpdates({});
      setIsEditing(false);
    } catch {
      // Reset failed — stay in edit mode
    }
  }, [resetMutation, queryClient, dashboardName]);

  return {
    widgets,
    layout,
    isLoading: preferenceQuery.isLoading || sharedQuery.isLoading,
    isEditing,
    setIsEditing,
    onLayoutChange,
    addWidget,
    updateWidget,
    removeWidget,
    saveLayout,
    resetToDefaults,
    isSaving: saveMutation.isPending,
  };
}
